import React, { useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faCalendarDay,
	faClock,
	faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { CCBadge, CCEmptyState } from "@pymblecitizencoders/cc-ui";
import { CalendarEvent } from "../../types/event";
import { EventChip, chipClass, chipsFor } from "../../utils/eventChips";
import { hexToRgbTriple } from "../../utils/eventColour";
import { DayFocus, groupByDay } from "../../utils/eventDays";
import { formatCardDate, formatTimeRange } from "../../utils/eventDates";
import { useDayWindow } from "../../hooks/useDayWindow";
import { Tooltip } from "../common/Tooltip";
import { ChipDetail } from "./ChipDetail";
import { BottomEdge, SearchEdges, TopEdge } from "./browseEdges";
import "./eventManager.css";

interface EventCardsGridProps {
	/** Already filtered by the rail — this component does no filtering. */
	events: CalendarEvent[];
	onOpen: (id: string) => void;
	/** The day to start at, and the request that asked for it. See DayFocus. */
	focus?: DayFocus | null;
	/** Fetch the month before the earliest one loaded. */
	onLoadEarlier?: () => void;
	/** Fetch the month after the latest one loaded. */
	onLoadLater?: () => void;
	loadingEarlier?: boolean;
	loadingLater?: boolean;
	/**
	 * Present when these events came from a server-side search rather than from
	 * the month pager. The two page over different things, so the edges say
	 * different things: a result set has no earlier months to fetch, only more
	 * of itself. See browseEdges.
	 */
	search?: SearchEdges;
	/**
	 * Present only while a tag is armed for assignment. It changes what a click
	 * on a card MEANS — choose rather than open — which is why it arrives as one
	 * object rather than as a loose flag: there is no state in which half of it
	 * applies.
	 */
	selection?: CardSelection;
}

/** The selection the page owns; this view only reports clicks against it. */
export interface CardSelection {
	selected: Set<string>;
	onToggle: (id: string) => void;
}

/**
 * The Cards view: the same events as the List, laid out as tiles.
 *
 * Flat and sorted by start rather than grouped under headings. The date is on
 * every card because a grid has no row above it to carry one — which is the
 * whole difference between this and the List, and why both exist: the List is
 * for reading a day, the grid is for scanning a range.
 *
 * It windows BY DAY all the same, sharing useDayWindow with the List, and then
 * flattens. Paging by whole days rather than by card count means the two views
 * move through time at the same rate, so switching between them lands in the
 * same week rather than somewhere else entirely.
 *
 * The card is one big button. Unlike the List row, there is nothing else on a
 * card to click, so making the whole tile the target is both the larger hit
 * area and the simpler thing to announce.
 */
const EventCardsGridInner: React.FC<EventCardsGridProps> = ({
	events,
	onOpen,
	selection,
	focus,
	onLoadEarlier,
	onLoadLater,
	loadingEarlier,
	loadingLater,
	search,
}) => {
	const days = useMemo(() => groupByDay(events), [events]);
	const rootRef = useRef<HTMLDivElement>(null);

	const { window, topRef, bottomRef, atStart, atEnd, canScroll } = useDayWindow(
		{
			days,
			rootRef,
			focus,
			/* Undefined in search mode: reaching the end of the results must not
			   ask the month pager for another month, which would fetch a range
			   nobody asked for and mix it into a result set. */
			onNeedEarlier: search ? undefined : onLoadEarlier,
			onNeedLater: search ? undefined : onLoadLater,
			loadingEarlier,
			loadingLater,
		},
	);

	const shown = useMemo(() => window.flatMap((g) => g.events), [window]);

	/** Chips once per event rather than once per render — see EventListRows. */
	const chips = useMemo(() => {
		const map = new Map<string, EventChip[]>();
		for (const evt of events) map.set(evt._id ?? String(evt.id), chipsFor(evt));
		return map;
	}, [events]);

	if (days.length === 0) {
		return (
			<CCEmptyState
				icon={<FontAwesomeIcon icon={faCalendarDay} />}
				message={
					search ? "Nothing matches this search." : "No events in this range."
				}
				description={
					search
						? "The whole calendar was searched, so there is no other month to try. Use a different word, or clear a filter in the rail."
						: "Try another month, or clear a filter in the rail."
				}
			/>
		);
	}

	return (
		<div className="em-cards" ref={rootRef}>
			{/* See EventListRows on the two sentinels. `--grid` spans the columns:
			    this is a grid container, so a full-width child has to say so. */}
			<div className="em-browse-edge em-browse-edge--grid" ref={topRef}>
				<TopEdge
					atEdge={atStart}
					search={search}
					browse={{ loadingEarlier, onLoadEarlier, scrollable: canScroll }}
				/>
			</div>

			{shown.map((evt) => {
				const id = evt._id ?? String(evt.id);
				const rgb = hexToRgbTriple(evt.color);
				const start = new Date(evt.start ?? "");
				const date = Number.isNaN(start.getTime())
					? "Unscheduled"
					: formatCardDate(start);
				const time = formatTimeRange(evt.start, evt.end);
				const banner = evt.Banner?.ImageUrl;
				const picked = selection?.selected.has(id) ?? false;
				return (
					<button
						type="button"
						className={`em-card${picked ? " em-card--picked" : ""}`}
						key={id}
						style={{ borderLeftColor: `rgb(${rgb})` }}
						/* While a tag is armed the card CHOOSES rather than opens.
						   Leaving open on the click and putting choose somewhere else
						   would mean a tick target of its own on every card, which is
						   the one thing a tile has no room for — and the armed bar
						   above already says what a click will do. */
						aria-pressed={selection ? picked : undefined}
						onClick={() => (selection ? selection.onToggle(id) : onOpen(id))}
					>
						{/* Only when there is one, as in the calendar: a placeholder on
						    every event without a banner spends the same room saying
						    nothing, and most events have none. A card with no picture
						    simply starts at its date. */}
						{banner && <img className="em-card__banner" src={banner} alt="" />}
						{/* The padding lives HERE, not on the card, so the banner above
						    is simply the card's width. Cancelling the card's padding
						    with negative margins instead meant the picture's width was
						    an arithmetic result that had to be kept in step with it,
						    and an `<img>` will not stretch to a flex line the way a
						    div does — it sizes from its own aspect ratio. */}
						<span className="em-card__body">
							<span className="em-card__when">
								<span className="em-card__date">
									<FontAwesomeIcon icon={faCalendarDay} aria-hidden="true" />{" "}
									{date}
								</span>
								{time && (
									<span className="em-card__time">
										<FontAwesomeIcon icon={faClock} aria-hidden="true" /> {time}
									</span>
								)}
							</span>

							<span className="em-card__title">{evt.title}</span>

							{evt.location && (
								<span className="em-card__location">
									<FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />{" "}
									{evt.location}
								</span>
							)}

							<span className="em-card__chips">
								{(chips.get(id) ?? []).map((chip) =>
									/* See EventListRows on why this is the app's Tooltip and
									   not a `title`. */
									chip.detail ? (
										<Tooltip
											key={chip.id}
											label={chip.label}
											details={<ChipDetail lines={chip.detail} />}
											className="em-chip-trigger"
										>
											<CCBadge tone={chip.tone} className={chipClass(chip)}>
												{chip.label}
											</CCBadge>
										</Tooltip>
									) : (
										<CCBadge
											key={chip.id}
											tone={chip.tone}
											className={chipClass(chip)}
										>
											{chip.label}
										</CCBadge>
									),
								)}
							</span>
						</span>
					</button>
				);
			})}

			<div className="em-browse-edge em-browse-edge--grid" ref={bottomRef}>
				<BottomEdge
					atEdge={atEnd}
					search={search}
					browse={{ loadingLater, onLoadLater, scrollable: canScroll }}
				/>
			</div>
		</div>
	);
};

/** Memoised for the same reason as EventListRows. */
export const EventCardsGrid = React.memo(EventCardsGridInner);
